// Infografía del ciclo completo del catering, para la Ayuda del módulo.
// Diseño validado con el panel de expertos (operaciones + logística):
//  · CARRILES POR FRECUENCIA, no una línea: mezclar ritmos es lo que más confunde.
//  · DOS compras distintas: base de no perecibles (antes, contra mínimos) y
//    perecibles (después de confirmar pax, vía MRP).
//  · Tres estados visuales y una regla dura: una caja que el sistema NO hace
//    nunca lleva enlace — así el diagrama no promete funcionalidad inexistente.
//  · Los controles de temperatura van EN el flujo (son compuertas, no adornos).
//  · El ciclo no termina en la factura: se retroalimenta.
import { useState } from 'react';
import {
  AlertTriangle, ArrowRight, CircleDot, ExternalLink, RotateCcw, Thermometer,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { useTabsStore } from '@/stores/tabs.store';

type Estado = 'sistema' | 'manual' | 'roadmap';

interface Etapa {
  n: number;
  titulo: string;
  entregable: string;
  control: string;
  estado: Estado;
  ruta?: string;
  tituloTab?: string;
  critico?: boolean;
  temp?: boolean;
  nota?: string;
}

interface Carril {
  ritmo: string;
  cuando: string;
  acento: string;
  etapas: Etapa[];
}

const CARRILES: Carril[] = [
  {
    ritmo: 'Planifico',
    cuando: 'Anual / mensual',
    acento: 'border-l-sky-500',
    etapas: [
      { n: 1, titulo: 'Recetario y fichas técnicas', entregable: 'Ficha versionada con costo por porción', control: 'Sin precio completo, el costo sale incompleto', estado: 'sistema', ruta: '/catering/recetas', tituloTab: 'Recetario' },
      { n: 2, titulo: 'Menús y rotación del año', entregable: 'Los 14 menús y su calendario', control: 'Ninguna celda sin ficha (adiós platos a $0)', estado: 'sistema', ruta: '/catering/menus', tituloTab: 'Menús cíclicos' },
      { n: 3, titulo: 'Contratos con clientes', entregable: 'Precios por servicio, horarios y corte', control: 'Vigencia y precio pactado aprobados', estado: 'sistema', ruta: '/catering/contratos', tituloTab: 'Contratos y pax' },
      { n: 4, titulo: 'Compra base de NO perecibles', entregable: 'Orden de compra de arroz, aceite, granos', control: 'Contra stock mínimo, no «por si acaso»', estado: 'sistema', ruta: '/inventario/reposicion', tituloTab: 'Reposición', nota: 'No depende del pax de mañana: se repone por mínimos.' },
    ],
  },
  {
    ritmo: 'Abastezco',
    cuando: 'Semanal',
    acento: 'border-l-violet-500',
    etapas: [
      { n: 5, titulo: 'Programación de la semana', entregable: 'Qué menú del ciclo corre cada día, por contrato', control: 'Sustituciones y feriados sin romper el menú maestro', estado: 'sistema', ruta: '/catering/calendario', tituloTab: 'Calendario de rotación' },
      { n: 6, titulo: 'Confirmación de pax con el cliente', entregable: 'Proyección de servicios por contrato', control: 'Sin pax no hay cálculo de compra', estado: 'sistema', ruta: '/catering/contratos', tituloTab: 'Contratos y pax' },
      { n: 7, titulo: 'Necesidad de compra (MRP)', entregable: 'Menú × pax − stock = qué comprar', control: 'Descuenta stock real, no teórico', estado: 'sistema', ruta: '/catering/contratos', tituloTab: 'Contratos y pax' },
      { n: 8, titulo: 'Adjudicación al proveedor', entregable: 'A quién le compro cada ítem, y a qué precio acordado', control: 'Comparar por $/kg útil, no por bulto', estado: 'sistema', ruta: '/inventario/precios-compra', tituloTab: 'Precios de compra', nota: 'Ya puedes fijar el PRECIO ACORDADO por proveedor y ver la variación y la tendencia por ítem. El comparativo de canasta completa entre proveedores queda para una próxima fase.' },
      { n: 9, titulo: 'Recepción de perecibles', entregable: 'Ingreso al kardex con lote y caducidad', control: 'Precio dentro de lo acordado · peso y calidad reales', estado: 'sistema', ruta: '/inventario/recepcion', tituloTab: 'Recepción', critico: true, temp: true, nota: 'El control de precios avisa en el momento si el proveedor subió sin decirlo (y protege el costeo del menú), sin frenar el descargue. El doble peso báscula vs. factura y el rechazo con foto quedan para una próxima fase.' },
      { n: 9.5, titulo: 'Revisión de desviaciones de precio', entregable: 'Alza aceptada con motivo, o reclamo al proveedor', control: 'Quien recibe no aprueba su propia desviación', estado: 'sistema', ruta: '/inventario/precios-compra', tituloTab: 'Precios de compra', nota: 'Mientras la desviación no se apruebe, el costo con que se costean las recetas no se actualiza: un precio anómalo no envenena los 14 menús.' },
      { n: 10, titulo: 'Guardado en su zona', entregable: 'Seca / refrigerado / congelado', control: 'FEFO: primero lo que vence antes', estado: 'sistema', ruta: '/inventario/stock-ubicacion', tituloTab: 'Stock por ubicación', temp: true },
    ],
  },
  {
    ritmo: 'Produzco y entrego',
    cuando: 'Diario',
    acento: 'border-l-emerald-500',
    etapas: [
      { n: 11, titulo: 'Pedido del día / efectivos', entregable: 'Pax confirmados por cliente y servicio', control: 'Cierre de captura a hora fija', estado: 'sistema', ruta: '/catering/contratos', tituloTab: 'Contratos y pax', critico: true },
      { n: 12, titulo: 'Orden de producción y requisición', entregable: 'Batches consolidados + MP a retirar', control: 'Consumo por FEFO, no sale lote sin registrar', estado: 'sistema', ruta: '/catering/produccion', tituloTab: 'Producción del día' },
      { n: 13, titulo: 'Producción en cocina', entregable: 'Batches con estado, temperatura y hora', control: 'Temperatura del PCC: si no alcanza, no avanza', estado: 'sistema', ruta: '/catering/piso', tituloTab: 'Torre de control', temp: true },
      { n: 14, titulo: 'Porcionado y empaque por cliente', entregable: 'Bandejas/termos contados por servicio', control: 'Cuadre porciones vs. pax · dietas separadas', estado: 'sistema', ruta: '/catering/piso', tituloTab: 'Torre de control', nota: 'El conteo por ruta y la etiqueta por contenedor quedan para una próxima fase.' },
      { n: 15, titulo: 'Despacho y carga', entregable: 'Hoja de carga por ruta + guía de remisión SRI', control: 'Nada sale sin temperatura de salida y contenedores', estado: 'sistema', ruta: '/catering/despacho', tituloTab: 'Despacho del día', temp: true, critico: true, nota: 'Arma la carga por ruta desde el pax del día, aplica el checklist de salida (bloqueante) y emite la guía de remisión electrónica (codDoc 06) a los clientes. La app del repartidor con evidencia de entrega es la etapa siguiente.' },
      { n: 16, titulo: 'Distribución y entrega', entregable: 'Hora real, temperatura de llegada, firma del receptor y foto', control: 'Los datos que prueban la entrega (POD)', estado: 'sistema', ruta: '/catering/despacho', tituloTab: 'Despacho del día', temp: true, nota: 'El repartidor abre su ruta del día en el celular por un enlace/QR sin login (/reparto/token) y registra cada parada: temperatura de llegada, firma del receptor, foto e incidencias. La oficina lo ve en vivo y puede registrar de respaldo. El seguimiento de rutas en mapa (GPS) queda para más adelante.' },
      { n: 17, titulo: 'Retorno de contenedores y línea', entregable: 'Termos devueltos y lo que volvió sin servir', control: 'Contenedores completos', estado: 'sistema', ruta: '/catering/menaje', tituloTab: 'Menaje y sobrantes', nota: 'Al cerrar el despacho se registra el menaje devuelto por cliente (el saldo sin devolver es el activo en riesgo, valorizado en $) y los sobrantes por servicio. El saldo por cliente y el análisis de sobrantes están en Menaje y sobrantes.' },
      { n: 18, titulo: 'Cierre del día', entregable: 'Porciones reales, rendimiento y merma', control: 'La orden no se cierra sin porciones reales', estado: 'sistema', ruta: '/catering/produccion', tituloTab: 'Producción del día', critico: true },
    ],
  },
  {
    ritmo: 'Cobro y aprendo',
    cuando: 'Quincenal / mensual',
    acento: 'border-l-amber-500',
    etapas: [
      { n: 19, titulo: 'Corte, acta de conformidad y factura', entregable: 'Servicios servidos × precio pactado → factura SRI', control: 'Acta firmada ANTES de facturar (el sistema lo bloquea)', estado: 'sistema', ruta: '/catering/cortes', tituloTab: 'Cortes y facturación', critico: true, nota: 'El corte agrupa lo SERVIDO por tipo de servicio y avisa los días sin confirmar. Registrada el acta de conformidad, factura respetando la política de emisión. El acta digital con firma del cliente en pantalla queda para una próxima fase.' },
      { n: 20, titulo: 'Costo real, food cost y margen', entregable: 'Margen por contrato y costo por porción', control: 'Revisión gerencial del período', estado: 'sistema', ruta: '/catering/dashboard', tituloTab: 'Dashboard' },
      { n: 21, titulo: 'Costeo teórico vs real', entregable: 'Desviación por material y por día + ajustes de consumo', control: 'El teórico se ajusta a las porciones reales, no al plan', estado: 'sistema', ruta: '/catering/costeo', tituloTab: 'Costeo real', nota: 'Compara la ficha contra lo que salió de bodega: caza merma, sobre-porción y fichas mal calibradas. Los ajustes de consumo (retiro extra o devolución) mueven el kardex.' },
    ],
  },
];

// Los bucles que un flujo lineal no captura
const BUCLES: { de: string; a: string; texto: string }[] = [
  { de: 'Cierre del día', a: 'Recetario', texto: 'El rendimiento real recalibra las porciones por batch y el peso por porción: la ficha está viva, no es un documento muerto.' },
  { de: 'Merma y sobrantes', a: 'Necesidad de compra', texto: 'El stock y la merma reales ajustan el MRP siguiente y el % de merma del ingrediente.' },
  { de: 'Recepción', a: 'Recetario', texto: 'El costo por kilo REAL reemplaza al «precio promedio» en el escandallo — pero solo cuando la desviación fue aprobada: un precio anómalo no entra al costeo.' },
  { de: 'Alertas de precio', a: 'Precio acordado', texto: 'Al aceptar un alza se fija el nuevo precio acordado, así la excepción deja de ser excepción y la próxima compra no vuelve a alertar.' },
  { de: 'Costeo real vs teórico', a: 'Fichas y gramaje', texto: 'La desviación sistemática de un material (siempre se consume más que la ficha) recalibra la cantidad bruta y la merma del ingrediente: el escandallo deja de mentir.' },
  { de: 'Food cost y margen', a: 'Menús y contratos', texto: 'La ingeniería de menú rota los platos caros y dispara el reajuste de precio al cliente.' },
  { de: 'Devolución de línea', a: 'Gramaje y menú', texto: 'Lo que vuelve sin comerse dice qué plato no gusta o está sobre-porcionado.' },
  { de: 'Puntualidad del piso', a: 'Duración de las recetas', texto: 'Los tiempos reales de cocina corrigen la duración estándar con que se calcula la hora límite.' },
];

const ESTADO_CLS: Record<Estado, string> = {
  sistema: 'border-solid bg-card',
  manual: 'border-dashed bg-muted/30',
  roadmap: 'border-dashed bg-muted/20 opacity-80',
};
const ESTADO_ETIQUETA: Record<Estado, string> = {
  sistema: '', manual: 'manual hoy', roadmap: 'Próxima fase',
};

function EtapaCard({ e }: { e: Etapa }) {
  const openTab = useTabsStore((s) => s.openTab);
  const [abierto, setAbierto] = useState(false);
  // Regla dura: si el sistema no lo hace, NO hay enlace (el diagrama no promete)
  const enlazable = e.estado === 'sistema' && !!e.ruta;

  return (
    <div className={`relative rounded-lg border-2 p-2.5 text-left transition-colors ${ESTADO_CLS[e.estado]}
      ${e.critico ? 'border-red-400 dark:border-red-700' : ''}`}>
      <div className="flex items-start gap-2">
        <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold
          ${e.critico ? 'bg-red-600 text-white' : e.estado === 'sistema' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
          {e.n}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-sm font-semibold leading-tight">{e.titulo}</span>
            {e.temp && (
              <span title="Punto de control de temperatura" className="shrink-0">
                <Thermometer className="h-3.5 w-3.5 text-sky-600" />
              </span>
            )}
            {e.critico && (
              <span title="Aquí es donde más se rompe la cadena" className="shrink-0">
                <AlertTriangle className="h-3.5 w-3.5 text-red-600" />
              </span>
            )}
            {ESTADO_ETIQUETA[e.estado] && (
              <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                {ESTADO_ETIQUETA[e.estado]}
              </span>
            )}
          </div>
          <div className="pt-1 text-xs text-muted-foreground">
            <div><span className="font-medium text-foreground/70">Genera:</span> {e.entregable}</div>
            <div className={e.critico ? 'text-red-700 dark:text-red-400' : ''}>
              <span className="font-medium text-foreground/70">No se salta:</span> {e.control}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            {enlazable && (
              <button className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                onClick={() => openTab({ id: e.ruta!, title: e.tituloTab ?? e.titulo, path: e.ruta! })}>
                Abrir <ExternalLink className="h-3 w-3" />
              </button>
            )}
            {e.nota && (
              <button className="text-xs text-muted-foreground underline decoration-dotted"
                onClick={() => setAbierto((v) => !v)}>
                {abierto ? 'ocultar detalle' : 'ver detalle'}
              </button>
            )}
          </div>
          {abierto && e.nota && <p className="pt-1 text-xs text-muted-foreground">{e.nota}</p>}
        </div>
      </div>
    </div>
  );
}

export function FlujoCatering() {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <CircleDot className="h-4 w-4 text-primary" /> El ciclo del catering, de punta a punta
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Planifico → abastezco → confirmo → produzco → despacho → cobro → <b>aprendo</b>.
          Las etapas están agrupadas por <b>ritmo</b> (lo anual no se mezcla con lo diario) y cada una muestra
          qué genera y qué control no puede saltarse.
        </p>
        <div className="flex flex-wrap items-center gap-3 pt-2 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <span className="h-3 w-5 rounded border-2 border-solid bg-card" /> lo hace el sistema (con enlace)
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="h-3 w-5 rounded border-2 border-dashed bg-muted/30" /> procedimiento manual / próxima fase
          </span>
          <span className="inline-flex items-center gap-1"><Thermometer className="h-3.5 w-3.5 text-sky-600" /> control de temperatura</span>
          <span className="inline-flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5 text-red-600" /> aquí se rompe la cadena</span>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {CARRILES.map((c, ci) => (
          <div key={c.ritmo}>
            <div className={`mb-2 flex flex-wrap items-baseline gap-2 border-l-4 pl-2 ${c.acento}`}>
              <span className="text-sm font-bold uppercase tracking-wide">{c.ritmo}</span>
              <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">{c.cuando}</span>
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
              {c.etapas.map((e) => <EtapaCard key={e.n} e={e} />)}
            </div>
            {ci < CARRILES.length - 1 && (
              <div className="flex justify-center py-1 text-muted-foreground">
                <ArrowRight className="h-4 w-4 rotate-90" />
              </div>
            )}
          </div>
        ))}

        {/* Franja de cadena de frío/caliente: acompaña todo el tramo, no es un adorno */}
        <div className="rounded-lg border border-sky-300 bg-sky-50 p-3 text-sm dark:border-sky-800 dark:bg-sky-900/20">
          <div className="flex items-center gap-2 font-medium text-sky-900 dark:text-sky-200">
            <Thermometer className="h-4 w-4" /> La temperatura acompaña todo el flujo
          </div>
          <p className="pt-1 text-xs text-sky-900/80 dark:text-sky-200/80">
            Cinco compuertas, no cinco avisos: al <b>recibir</b> refrigerados, al <b>guardar</b> en cámara,
            al dar el batch por <b>listo</b> (temperatura interna del punto crítico), al <b>salir</b> del muelle
            y al <b>entregar</b> en el cliente. Entre la producción terminada y la carga corre además el
            <b> reloj de zona de peligro</b> (5–60 °C): ningún batch debe acumular más de 4 horas ahí — es la
            ventana donde de verdad se pierde un lote.
          </p>
        </div>

        {/* Los bucles: el ciclo no termina en la factura */}
        <div>
          <div className="mb-2 flex items-center gap-2 text-sm font-bold uppercase tracking-wide">
            <RotateCcw className="h-4 w-4 text-primary" /> El ciclo se retroalimenta
          </div>
          <p className="pb-2 text-xs text-muted-foreground">
            Lo que aprende cada día corrige lo que se planificó. Sin estos retornos, el sistema es un archivo;
            con ellos, se calibra solo.
          </p>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            {BUCLES.map((b) => (
              <div key={b.de + b.a} className="rounded-md border border-dashed p-2 text-xs">
                <div className="flex flex-wrap items-center gap-1 font-medium">
                  {b.de} <ArrowRight className="h-3 w-3 text-primary" /> {b.a}
                </div>
                <p className="pt-0.5 text-muted-foreground">{b.texto}</p>
              </div>
            ))}
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          Las etapas con borde punteado son procedimiento del cliente o están previstas para una próxima fase: no tienen
          enlace porque el sistema todavía no las ejecuta. Las cinco marcadas en rojo son donde la experiencia dice
          que más se rompe la cadena: recepción sin pesar, efectivos sin confirmar a tiempo, despacho sin temperatura
          de salida, cierre sin porciones reales y facturar sin acta firmada.
        </p>
      </CardContent>
    </Card>
  );
}
